import React, { useId, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BellRing, CheckCircle2, Loader2 } from 'lucide-react';
import { api, getErrorMessage } from '../api/client';
import { useSiteConfig } from './SiteBanner';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Footer "FY code update alerts" capture. Rendered on the dark navy footer.
 * Sends the email to POST /api/leads (CRM lead) and POST /api/newsletter/subscribe (consent-tracked list).
 * Succeeds when either call succeeds; both are idempotent server-side.
 */
export default function LeadCapture() {
  const config = useSiteConfig();
  const uid = useId();
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // honeypot: real users never see or fill it
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const [message, setMessage] = useState('');

  if (config && config.flags && config.flags.leadCapture === false) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      setStatus('error');
      setMessage('Enter a valid email address.');
      return;
    }
    setStatus('sending');
    setMessage('');
    try {
      const [lead, news] = await Promise.allSettled([
        api.post('/leads', { email: value, source: 'footer', website }),
        api.post('/newsletter/subscribe', { email: value, source: 'footer', website }),
      ]);
      if (lead.status === 'fulfilled' || news.status === 'fulfilled') {
        setStatus('done');
        return;
      }
      throw lead.reason || news.reason;
    } catch (err) {
      setStatus('error');
      const code = err && err.response && err.response.status;
      setMessage(code === 429 ? 'Too many attempts. Please try again in a few minutes.' : getErrorMessage(err, 'Could not subscribe. Please try again.'));
    }
  };

  const inputId = `${uid}-email`;
  const msgId = `${uid}-msg`;

  return (
    <div className="mt-5 max-w-sm">
      <AnimatePresence mode="wait" initial={false}>
        {status === 'done' ? (
          <motion.div
            key="done"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex items-start gap-3 rounded-2xl border border-on-navy/15 bg-on-navy/[0.06] p-3.5 text-sm text-on-navy/80"
            role="status"
          >
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-sun" aria-hidden="true" />
            <span>
              <b className="font-display font-bold text-on-navy">You&rsquo;re on the list.</b> We&rsquo;ll email you when new fiscal-year ICD-10-CM code updates land.
            </span>
          </motion.div>
        ) : (
          <motion.form key="form" initial={false} exit={{ opacity: 0 }} onSubmit={onSubmit} noValidate aria-describedby={message ? msgId : undefined}>
            <label htmlFor={inputId} className="flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.16em] text-on-navy/60">
              <BellRing className="h-3.5 w-3.5 text-sun" aria-hidden="true" /> Get FY code update alerts
            </label>
            <div className="mt-2.5 flex gap-2 rounded-full border border-on-navy/20 bg-on-navy/[0.06] p-1 transition-colors focus-within:border-sun/70 focus-within:bg-on-navy/[0.1]">
              <input
                id={inputId}
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                maxLength={200}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (status === 'error') setStatus('idle');
                }}
                placeholder="you@practice.com"
                aria-invalid={status === 'error' || undefined}
                className="min-w-0 flex-1 bg-transparent px-3.5 text-sm text-on-navy placeholder:text-on-navy/45 focus:outline-none"
              />
              {/* Honeypot: hidden from people and assistive tech. */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                className="absolute -left-[9999px] h-px w-px opacity-0"
              />
              <button type="submit" className="btn-sun btn-sm shrink-0" disabled={status === 'sending'}>
                {status === 'sending' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                {status === 'sending' ? 'Sending' : 'Notify me'}
              </button>
            </div>
            <p id={msgId} className={`mt-2 min-h-[1.25rem] text-xs ${status === 'error' ? 'font-semibold text-pop' : 'text-on-navy/55'}`} aria-live="polite">
              {status === 'error' ? message : 'FY code updates and product news. No spam, unsubscribe anytime.'}
            </p>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
